import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Modal, ModalFooter, ModalHeader, TextField } from '@parspec/pixel';

import { useEditFolderForProject, useEditUploadedFilesForProject } from '../queries';

interface IRenameModalProps {
    open: boolean;
    handleCloseRenameModal: () => void;
    handleUploadedFileName: (name: string) => void;
    uploadedFileDetails: { id: number; name: string; isFolder?: boolean };
}

const RenameModal = (props: IRenameModalProps) => {
    const { open, handleCloseRenameModal, uploadedFileDetails, handleUploadedFileName } = props;
    const { projectId } = useParams();

    const { isFolder = false } = uploadedFileDetails;

    const [fileName, setFileName] = useState<string>("");
    const [requiredError, setRequiredError] = useState<string | null>(null);

    useEffect(() => {
        setFileName(uploadedFileDetails.name);
    }, [uploadedFileDetails.name]);

    const { mutateAsync: editFile, isLoading: isFileEditing } = useEditUploadedFilesForProject();
    const { mutateAsync: editFolder, isLoading: isFolderEditing } = useEditFolderForProject();

    const isLoading = isFolder ? isFolderEditing : isFileEditing;

    const handleConfirmClicked = async () => {
        const trimmedName = fileName?.trim();
        if (!trimmedName) {
            setRequiredError('Required');
            return;
        }

        try {
            isFolder ? await editFolder({
                projectId: Number(projectId),
                folderId: uploadedFileDetails.id,
                payload: { name: trimmedName }
            }) : await editFile({
                projectId: Number(projectId),
                documentId: uploadedFileDetails.id,
                payload: { filename: trimmedName }
            });

            handleUploadedFileName(trimmedName);
            handleCloseRenameModal();
        } catch (error) { }
    };

    const header = <ModalHeader title="Rename" onClose={handleCloseRenameModal} />;
    const footer = (
        <ModalFooter
            onReject={handleCloseRenameModal}
            onAccept={handleConfirmClicked}
            continueButtonLabel="Confirm"
            cancelButtonLabel="Cancel"
            isLoading={isLoading}
        />
    );

    const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.value);
        setRequiredError(null);
    };

    const handleOnBlur = () => {
        if (!fileName?.trim().length) {
            setRequiredError('Required');
        }
    };

    return (
        <Modal header={header} footer={footer} open={open}>
            <Box width={480}>
                <TextField
                    disabled={isLoading}
                    label={isFolder ? "Folder Name*" : "File Name*"}
                    onChange={handleFileNameChange}
                    value={fileName}
                    onBlur={handleOnBlur}
                    helperText={requiredError}
                    error={!!requiredError}
                />
            </Box>
        </Modal>
    );
};

export default RenameModal;
